import { useLazyQuery } from '@apollo/client'
import { isEmpty } from 'lodash-es'
import { useEffect, useState } from 'react'

import { gql } from '../../__generated__/graphql/catalog/gql'
import { useGoogleTagManager } from '../../lib/hooks/useGoogleTagManager'

/**
 * Request variant information & track product impressions for all of the items in a collection.
 *
 * This (essentially) stateless hook exists to avoid unnecessary rerenders for a collection page.
 * By encapsulating the data loading & tracking behavior associated with tracking product impressions,
 * we are able to avoid re-renders for functionality that is not related  to the presentation of the collections page.
 */
export function useCollectionViewProductImpressions({
  collectionViewHandle,
  skusViewed,
}: {
  collectionViewHandle: string
  skusViewed: string[]
}) {
  const { pushProductImpressions } = useGoogleTagManager()
  const [skusSent, _] = useState(() => {
    return new Set<string>()
  })
  const [fetchVariantsForSkus] = useLazyQuery(
    gql(`
      query GetVariantsForImpressionEvents($skus: [String!]!) {
        productCatalogVariants(skus: $skus) {
          __typename
          id
          sku
          shopifyId
          shopifyProductId
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
          }
          group {
            primaryCategory {
              analyticsId
            }
            product {
              handle
            }
          }
        }
      }
    `)
  )

  useEffect(() => {
    const skusToSend: string[] = []
    skusViewed.forEach(sku => {
      if (!skusSent.has(sku)) {
        skusToSend.push(sku)
        skusSent.add(sku)
      }
    })

    const handleProductImpressions = async () => {
      // Query to get the default variants for the items in a given collection/sectionId
      const { data } = await fetchVariantsForSkus({
        variables: {
          skus: skusToSend,
        },
      })

      // If we receive items from the query, push product impressions
      if (data && !isEmpty(data.productCatalogVariants)) {
        pushProductImpressions({
          variants: data.productCatalogVariants.map(variant => {
            return {
              ...variant,
              product: variant.group.product,
              variantGroup: variant.group,
            }
          }),
          list: collectionViewHandle,
        })
      }
    }

    if (!isEmpty(skusToSend)) handleProductImpressions()
  }, [collectionViewHandle, fetchVariantsForSkus, pushProductImpressions, skusSent, skusViewed])
}
